import { AccommodationModalDetailedProps } from "@/components/AccommodationModalDetailed/AccommodationModalDetailedProps.ts";
import Gallery from "@/components/Gallery/Gallery.tsx";
import { Close } from "@/components/Icons/utility/Close.tsx";
import { RatesPicker } from "@/components/Inputs/RatesPicker/RatesPicker.tsx";
import { PriceAndCapacity } from "@/components/PriceAndCapacity/PriceAndCapacity.tsx";
import { useGetLowestPriceFrom } from "@/helpers/getLowestPriceFrom.ts";
import { useGTMAccommodationPreviewOpen } from "@/helpers/gtm/useGTMAccommodationPreviewOpen.ts";
import { useGetAmenityTranslated } from "@/helpers/translation/useGetAmenityTranslated.ts";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const AccommodationModalDetailed = (props: AccommodationModalDetailedProps) => {
  const { t } = useTranslation();
  const rg = useMemo(() => props.previewRG!, [props?.previewRG]);
  const { description, name } = useGetRGTranslated(rg);
  const amenities = useGetAmenityTranslated(rg?.amenities);
  const lowestPriceFrom = useGetLowestPriceFrom(rg);
  useGTMAccommodationPreviewOpen(rg);

  const handleRenderDetails = () => {
    if (!description) return null;

    return (
      <div className="c-accommodation-modal__info-details">
        <h4 className="u-p0 u-fw-600">{t("details")}</h4>
        <p className="u-p0 c-accommodation__info-description">{description}</p>
      </div>
    );
  };

  const handleRenderAmenities = () => {
    if (!amenities?.length) return null;

    return (
      <div className="accModalDetailedImgFooter c-accommodation-modal__info-amenities">
        <h4 className="u-p0 u-fw-600">{t("amenities")}</h4>
        <ul className="c-amenities u-p0">{amenities?.map((amenity, i) => <li key={i}>{amenity}</li>)}</ul>
      </div>
    );
  };

  const sortedImages = useMemo(() => {
    const sortedPerGalleryPositionIndex = rg.images.sort((a, b) => {
      return (a.galleryPositionIndex ?? 0) - (b.galleryPositionIndex ?? 0);
    });

    const primaryImage = sortedPerGalleryPositionIndex.find((image) => image.isPrimary);
    if (primaryImage) {
      const filteredImages = sortedPerGalleryPositionIndex.filter((image) => !image.isPrimary);
      return [primaryImage, ...filteredImages];
    }
    return sortedPerGalleryPositionIndex;
  }, [rg.images]);

  return (
    <div className="c-accommodation-modal-wrapper">
      <div className="c-accommodation-modal-wrapper__close">
        <button className="c-close-button" onClick={props.handleCloseModal}>
          <Close />
        </button>
      </div>
      <div className="accModalDetailed c-accommodation-modal">
        <div className="accModalDetailedImgSection c-accommodation-modal__gallery">
          <Gallery slides={sortedImages} options={{ loop: true }} thumbnails />
        </div>
        <div className="accModalDetailedInfoSection c-accommodation-modal__info">
          <div className="c-accommodation-modal__info-inner">
            <h3 className="u-h5 u-fw-600 c-accommodation-modal__info-title">{name}</h3>
            <PriceAndCapacity
              priceFrom={lowestPriceFrom}
              capacity={rg.guestCapacity}
              additionalCapacity={rg.guestAdditionalCapacity}
            />
            {handleRenderDetails()}
            {handleRenderAmenities()}
          </div>
        </div>
      </div>
      {props.hideRatesPicker || (
        <div className="c-accommodation-modal-rates">
          <RatesPicker rateGroup={rg} onSelectRatePlan={props.handleCloseModal} />
        </div>
      )}
    </div>
  );
};
